/**
 * O acervo de atestados do Nectar, inteiro e à vista.
 *
 * Pedido do Pedro, 2026-09-29: "um lugar para olhar todos os atestados que o
 * NECTAR tem". Eles estavam no banco desde a primeira carga e sustentam a
 * parte maior do score — a afinidade, o maior peso de todos, sai deles —, mas
 * a interface só os mostrava de lado, como os atestados que sustentam o score
 * de uma licitação. Quem queria saber o que o acervo TEM precisava do terminal
 * (`nectuno-ficha`).
 *
 * A lista e a ficha leem a mesma rota que o terminal lê, que lê a view
 * `atestado_ficha`: a data já vem escrita ("03/07/2003" ou "2004") e a
 * natureza já vem decidida. Esta tela não formata de novo o que o banco já
 * formatou.
 *
 * ── a natureza é o que não pode passar despercebido ─────────────────────
 *
 * Declaração e nota fiscal estão no acervo com os mesmos campos preenchidos
 * que um atestado, e não comprovam capacidade técnica nenhuma. Numa lista em
 * que todos os cartões têm a mesma cara, a declaração de idoneidade seria lida
 * como mais um atestado. Por isso ela ganha etiqueta, cor e a barra lateral —
 * e a frase por extenso, porque no toque não há `title`.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): na lista, o cabeçalho, a busca, as abas
 * do tipo de documento, os dois seletores, o "Limpar filtros", a troca de
 * organização (botões de ícone), as etiquetas, o aviso de erro, o vazio, a
 * nota e as seções dos grupos saem de `src/ds`. Ficam em `Atestados.module.css`
 * o que é desta tela — a faixa dos números com a explicação recolhida, a grade
 * e o cartão do atestado — e a ficha, que segue a folha de leitura do detalhe.
 */
import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  api,
  ErroApi,
  type Ficha,
  type Natureza,
  type OportunidadeDoAtestado,
  type PaginaFichas,
  type UsoDoAtestado,
} from "../api/cliente";
import { Migalhas } from "../componentes/Migalhas";
import { Secao } from "../componentes/Secao";
import { Nota } from "../componentes/Selos";
import {
  Abas, Aviso, Botao, BotaoIcone, Busca, Cabecalho, Etiqueta, IconeGrade, IconeInfo, IconeLinhas,
  Nota as NotaDs, Secao as SecaoDs, Seletor, Vazio,
} from "../ds";
import painel from "../componentes/PainelDetalhe.module.css";
import selos from "../componentes/Selos.module.css";
import pagina from "./Licitacao.module.css";
import css from "./Atestados.module.css";

/** A ordem das pílulas é a do peso: o que comprova primeiro. */
const NATUREZAS: { valor: Natureza; um: string; varios: string }[] = [
  { valor: "atestado", um: "atestado", varios: "Atestados" },
  { valor: "declaracao", um: "declaração", varios: "Declarações" },
  { valor: "nota fiscal", um: "nota fiscal", varios: "Notas fiscais" },
  // O PROJETO EXECUTADO QUE A GESTÃO INFORMOU (D14, migração 0124): o serviço
  // existiu, e o atestado dele não está no sistema. Cruza e pontua, e não é o
  // documento que a comissão confere.
  { valor: "projeto", um: "projeto executado", varios: "Projetos sem atestado" },
];

const NAO_COMPROVA = "não comprova capacidade técnica";
const SEM_ATESTADO = "sem atestado no sistema";

const nomeDaNatureza = (n: Natureza) => NATUREZAS.find((x) => x.valor === n)?.um ?? n;

/** A frase que acompanha o que não é atestado — a do projeto é outra. */
const ressalvaDaNatureza = (n: Natureza) =>
  n === "projeto"
    ? "Projeto executado que a gestão informou. O atestado dele não está no sistema: tem de ser obtido com o cliente."
    : `A ${nomeDaNatureza(n)} ${NAO_COMPROVA}.`;

/**
 * UMA CHAVE para a lista, por texto buscado. A lista inteira (busca vazia) e
 * a buscada são a mesma consulta com parâmetros diferentes; com a mesma forma
 * de chave, a tela sem busca faz UMA requisição só, e a ficha acha o atestado
 * no cache sem pedir de novo.
 */
const chaveLista = (busca: string) => ["atestados", busca] as const;
const pedeLista = (busca: string) => () =>
  api.atestados({ busca: busca || undefined, quantidade: 1000 });

/**
 * A ORDEM DA TELA: do mais recente para o mais antigo, e não a do servidor.
 *
 * O servidor devolve em ordem crescente, que é a do terminal — ali se lê de
 * cima para baixo como uma história. Aqui a pergunta é outra ("o que fizemos
 * por último?"), e o recente vem primeiro.
 *
 * O documento que só declara o ano fica NO ANO DELE, depois dos que têm o
 * dia. O `data_documento` dele é 1º de janeiro, e ordenar por essa data o
 * poria abaixo de todo o resto do ano como se fosse de janeiro — que é a
 * afirmação sobre o dia que a `data_precisao` existe para não fazer. Sem dia,
 * ele não tem lugar dentro do ano; tem o fim do ano, como o sem data tem o fim
 * da lista.
 */
function maisRecentePrimeiro(a: Ficha, b: Ficha): number {
  const porCodigo = a.codigo.localeCompare(b.codigo, "pt-BR", { numeric: true });
  if (!a.data_documento || !b.data_documento) {
    if (a.data_documento) return -1;
    if (b.data_documento) return 1;
    return porCodigo;
  }
  const ano = b.data_documento.slice(0, 4).localeCompare(a.data_documento.slice(0, 4));
  if (ano) return ano;
  const soAnoA = a.data_precisao === "ano";
  const soAnoB = b.data_precisao === "ano";
  if (soAnoA !== soAnoB) return soAnoA ? 1 : -1;
  // ISO (AAAA-MM-DD) ordena por texto, ao contrário de DD/MM/AAAA.
  return b.data_documento.localeCompare(a.data_documento) || porCodigo;
}

/** O código sem o `#`, para o caminho da URL — `/atestados/199`. */
const noCaminho = (codigo: string) => codigo.replace(/^#/, "");

/**
 * OS MAIS PRESENTES NOS CRUZAMENTOS PRIMEIRO (pedido do Pedro, 2026-09-29:
 * "ver quais são os mais úteis e presentes em todos os cruzamentos").
 *
 * Aparições primeiro, e depois as vezes como MELHOR PAR: desde a 0100 é o
 * primeiro par que decide a afinidade, e os outros só somam — entre dois que
 * aparecem igual, o que decide mais é o mais útil. Depois, quantas estão em
 * Avaliar; no empate de tudo, o mais recente.
 */
function maisPresentePrimeiro(uso: Map<string, UsoDoAtestado>) {
  return (a: Ficha, b: Ficha): number => {
    const ua = uso.get(a.codigo);
    const ub = uso.get(b.codigo);
    return (
      (ub?.aparicoes ?? 0) - (ua?.aparicoes ?? 0) ||
      (ub?.como_melhor ?? 0) - (ua?.como_melhor ?? 0) ||
      (ub?.em_avaliar ?? 0) - (ua?.em_avaliar ?? 0) ||
      maisRecentePrimeiro(a, b)
    );
  };
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/**
 * O PÉ DO CARTÃO: o quanto o atestado aparece nos cruzamentos, em três números
 * com o rótulo embaixo — oportunidades, como melhor par, em Avaliar —, que se
 * leem de relance e alinham de um cartão para o outro.
 */
function UsoNoCartao({ uso }: { uso?: UsoDoAtestado }) {
  if (!uso) {
    return <span className={`${css.uso} ${css.usoNada}`}>Não cruza com nenhuma oportunidade aberta</span>;
  }
  return (
    <span className={css.uso}>
      <span className={css.numero}>
        <b>{uso.aparicoes}</b>
        {uso.aparicoes === 1 ? "oportunidade" : "oportunidades"}
      </span>
      <span className={css.numero} title="É o primeiro par: o que decide a afinidade do score">
        <b>{uso.como_melhor}</b>
        melhor par
      </span>
      <span className={`${css.numero} ${uso.em_avaliar > 0 ? css.destaque : ""}`}>
        <b>{uso.em_avaliar}</b>
        em Avaliar
      </span>
    </span>
  );
}

function SeloNatureza({ natureza }: { natureza: Natureza }) {
  const prova = natureza === "atestado";
  return (
    <span
      className={`${selos.selo} ${prova ? selos.neutro : selos.atencao}`}
      title={prova ? "Atestado de capacidade técnica" : ressalvaDaNatureza(natureza)}
    >
      {nomeDaNatureza(natureza)}
    </span>
  );
}

/** As linhas de atuação, até `max` — o resto vira "+N", com todas no `title`. */
function Etiquetas({ linhas, max }: { linhas: string[]; max?: number }) {
  if (!linhas.length) return null;
  const vistas = max && linhas.length > max ? linhas.slice(0, max) : linhas;
  const resto = linhas.length - vistas.length;
  return (
    <span className={css.etiquetas}>
      {vistas.map((l) => (
        <Etiqueta key={l}>{l}</Etiqueta>
      ))}
      {resto > 0 && (
        <Etiqueta apagada title={linhas.join(" · ")}>
          +{resto}
        </Etiqueta>
      )}
    </span>
  );
}

/**
 * O CARTÃO DO ATESTADO, a mesma anatomia em todos (20/10, "mais clean e
 * organizada"): código e data numa linha discreta, o título, o órgão, até duas
 * linhas de atuação e, no pé, os três números do uso. O que não é atestado
 * (declaração, nota fiscal, projeto) ganha a etiqueta no canto e a frase por
 * extenso — sem a barra lateral colorida de antes.
 */
function CartaoDoAtestado({ f, uso }: { f: Ficha; uso?: UsoDoAtestado | "carregando" }) {
  const prova = f.comprova_capacidade;
  return (
    <Link
      to={`/atestados/${noCaminho(f.codigo)}`}
      // A ficha sabe que veio daqui: o "voltar" dela vira o voltar do
      // navegador, que devolve os filtros e a posição da lista.
      state={{ daLista: true }}
      className={css.cartao}
    >
      <span className={css.corpoCartao}>
        <span className={css.cabecaCartao}>
          <span className={css.codigo}>{f.codigo}</span>
          <span
            className={css.data}
            title={f.data_precisao === "ano" ? "O documento só declara o ano." : undefined}
          >
            {f.data ?? "sem data"}
          </span>
          {!prova && <SeloNatureza natureza={f.natureza} />}
        </span>
        <span className={css.titulo}>{f.titulo || "(sem título)"}</span>
        <span className={css.orgao}>
          {f.orgao ?? "Órgão não identificado"}
          {f.uf ? ` · ${f.uf}` : ""}
        </span>
        {!prova && (
          <span className={css.naoComprova}>
            {f.natureza === "projeto" ? SEM_ATESTADO : NAO_COMPROVA}
          </span>
        )}
        <Etiquetas linhas={f.linhas} max={2} />
      </span>
      {uso !== "carregando" && <UsoNoCartao uso={uso} />}
    </Link>
  );
}

export function Atestados() {
  const [params, setParams] = useSearchParams();
  // O FILTRO VIVE NA URL, como em Oportunidades: recarregar, voltar da ficha e
  // mandar o link para alguém devolvem a mesma lista.
  const busca = params.get("busca") ?? "";
  const linha = params.get("linha") ?? "";
  const natureza = params.get("natureza") ?? "";
  const ordem = params.get("ordem") === "cruzamentos" ? "cruzamentos" : "";

  // O campo é controlado, e a URL só muda depois de uma pausa na digitação:
  // disparar a cada tecla encheria o servidor de buscas pela metade.
  const [texto, setTexto] = useState(busca);

  // Atualização por FUNÇÃO, e não com os `params` desta renderização: o
  // `setTimeout` da busca dispara 250 ms depois, e com os params capturados
  // na hora ele desfaria um filtro de natureza clicado nesse meio-tempo.
  function muda(mudanca: Record<string, string>) {
    setParams(
      (atual) => {
        const p = new URLSearchParams(atual);
        for (const [chave, valor] of Object.entries(mudanca)) {
          if (valor) p.set(chave, valor);
          else p.delete(chave);
        }
        return p;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    const t = texto.trim();
    if (t === busca) return;
    const espera = setTimeout(() => muda({ busca: t }), 250);
    return () => clearTimeout(espera);
    // `busca` fica de fora de propósito: é a URL alcançando o campo, e não
    // alguém digitando — reagir a ela reagendaria a mesma busca.
  }, [texto]);

  // DUAS CONSULTAS, que sem busca são UMA (mesma chave). A inteira dá o
  // denominador ("12 de 201") e as linhas de atuação possíveis; a buscada é o
  // que a lista mostra.
  const todos = useQuery({ queryKey: chaveLista(""), queryFn: pedeLista("") });
  const buscados = useQuery({
    queryKey: chaveLista(busca),
    queryFn: pedeLista(busca),
    // Sem isto a lista pisca vazia a cada tecla que chega ao servidor.
    placeholderData: keepPreviousData,
  });
  // OS CRUZAMENTOS, numa consulta à parte: a lista aparece sem esperar por
  // eles, e a linha de uso de cada cartão chega logo depois.
  const uso = useQuery({ queryKey: ["atestados", "uso"], queryFn: api.usoDosAtestados });
  const usoPor = useMemo(
    () => new Map((uso.data?.itens ?? []).map((u) => [u.codigo, u])),
    [uso.data],
  );

  // AS LINHAS DE ATUAÇÃO SAEM DO ACERVO, e não de uma lista escrita aqui: a
  // união das `linhas` de todos os atestados. Da lista INTEIRA, e não da
  // buscada, para o seletor não perder opções enquanto se digita — e a linha
  // que veio na URL entra mesmo que nenhum atestado a tenha mais, porque
  // filtro aplicado e escondido é a lista encolhendo sem ninguém saber por quê.
  const linhasPossiveis = useMemo(() => {
    const todas = new Set<string>();
    for (const f of todos.data?.itens ?? []) f.linhas.forEach((l) => todas.add(l));
    if (linha) todas.add(linha);
    return [...todas].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [todos.data, linha]);

  // LINHA E NATUREZA FILTRAM AQUI, sobre o resultado da busca. O servidor
  // sabe filtrar pelas duas (e com a mesma regra: `linha = any(linhas)`,
  // `natureza =`), mas as contagens ao lado de cada opção precisam do
  // conjunto SEM aquele filtro — é assim que cada número diz o que o clique
  // vai mostrar. Pedindo ao servidor, seriam quatro consultas para o acervo
  // de ~201 linhas que já está inteiro na mão.
  const base = buscados.data?.itens;
  const { itens, porNatureza, porLinha } = useMemo(() => {
    const lista = base ?? [];
    const naLinha = (f: Ficha) => !linha || f.linhas.includes(linha);
    const naNatureza = (f: Ficha) => !natureza || f.natureza === natureza;
    const porNatureza = new Map<string, number>();
    const porLinha = new Map<string, number>();
    for (const f of lista) {
      if (naLinha(f)) porNatureza.set(f.natureza, (porNatureza.get(f.natureza) ?? 0) + 1);
      if (naNatureza(f)) f.linhas.forEach((l) => porLinha.set(l, (porLinha.get(l) ?? 0) + 1));
    }
    const itens = lista
      .filter((f) => naLinha(f) && naNatureza(f))
      .sort(ordem === "cruzamentos" ? maisPresentePrimeiro(usoPor) : maisRecentePrimeiro);
    // As contagens "todas" são o conjunto sem o próprio filtro, pelo mesmo
    // motivo das outras.
    porNatureza.set("", lista.filter(naLinha).length);
    porLinha.set("", lista.filter(naNatureza).length);
    return { itens, porNatureza, porLinha };
  }, [base, linha, natureza, ordem, usoPor]);

  const total = todos.data?.total ?? 0;
  // O PROJETO DECLARADO NÃO É ATESTADO (D14): contado junto, o título da tela
  // diria ter mais documentos do que o acervo tem.
  const projetos = todos.data?.itens.filter((f) => f.natureza === "projeto").length ?? 0;
  const documentos = total - projetos;
  const filtrando = !!(busca || linha || natureza);
  const erro = todos.error ?? buscados.error;

  function limpar() {
    setTexto("");
    // A ORDEM FICA: ela não é filtro, e limpar os filtros não deve devolver a
    // lista à ordem por data sem ninguém ter pedido.
    setParams(ordem ? { ordem } : {}, { replace: true });
  }

  const agrupar = params.get("agrupar") === "linha";

  // AGRUPADO POR LINHA DE ATUAÇÃO: o atestado com várias linhas aparece em
  // cada uma delas (a tela diz isso), e o que não tem nenhuma fica à parte.
  // As linhas com mais atestados vêm primeiro.
  const grupos = useMemo(() => {
    if (!agrupar) return [];
    const porLinha = new Map<string, Ficha[]>();
    const sem: Ficha[] = [];
    for (const f of itens) {
      if (!f.linhas.length) sem.push(f);
      for (const l of f.linhas) {
        if (linha && l !== linha) continue;
        porLinha.set(l, [...(porLinha.get(l) ?? []), f]);
      }
    }
    const ordenados = [...porLinha.entries()].sort(
      (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0], "pt-BR"),
    );
    return sem.length ? [...ordenados, ["Sem linha de atuação", sem] as [string, Ficha[]]] : ordenados;
  }, [agrupar, itens, linha]);

  const usoDe = (f: Ficha) => (uso.data ? usoPor.get(f.codigo) : "carregando" as const);

  return (
    <div className={css.tela}>
      <Cabecalho
        titulo="Atestados"
        descricao={
          !todos.data || !buscados.data
            ? "Lendo o acervo…"
            : filtrando
              ? `Mostrando ${itens.length} de ${total} ${projetos ? "itens do acervo" : "atestados"}`
              : "O que o Nectar já executou, com a prova assinada pelo cliente"
        }
        acoes={
          <Busca
            rotulo="Buscar nos atestados"
            placeholder="Buscar por título, atividade ou órgão…"
            value={texto}
            onChange={(e) => setTexto(e.currentTarget.value)}
          />
        }
      />

      {/* O ACERVO EM NÚMEROS, numa faixa só (20/10): eram quatro caixas do
          tamanho de cartões, competindo com a lista. Agora são números e
          rótulos lado a lado, separados por um filete, e a explicação de como
          o acervo é usado mora na ponta, recolhida. */}
      <div className={css.resumo}>
        <span className={css.dado}>
          <b>{todos.data ? documentos : "—"}</b>
          {documentos === 1 ? "atestado" : "atestados"}
        </span>
        {projetos > 0 && (
          <span className={css.dado} title={ressalvaDaNatureza("projeto")}>
            <b>{projetos}</b>
            {projetos === 1 ? "projeto sem atestado" : "projetos sem atestado"}
          </span>
        )}
        <span className={css.dado}>
          <b>{todos.data ? linhasPossiveis.length : "—"}</b>
          linhas de atuação
        </span>
        <span className={css.dado}>
          <b>{uso.data ? uso.data.oportunidades : "—"}</b>
          oportunidades abertas cruzam com o acervo
        </span>

        <details className={css.ajuda}>
          <summary>
            <IconeInfo tamanho={15} />
            Como o acervo é usado
          </summary>
          <div className={css.ajudaCorpo}>
            <p>
              É contra estes documentos que cada licitação é cruzada, e é deles que
              sai a afinidade do score. Declaração e nota fiscal também estão no
              acervo, mas {NAO_COMPROVA} — ficam marcadas.
              {projetos > 0 && (
                <>
                  {" "}
                  Os projetos executados que a gestão informou também entram no
                  cruzamento, e o atestado deles não está no sistema: ficam marcados
                  como projeto, e o documento tem de ser obtido com o cliente.
                </>
              )}
            </p>
            <p>
              {uso.data ? (
                <>
                  <b>Cruzamentos:</b>{" "}
                  {plural(uso.data.oportunidades, "oportunidade aberta tem", "oportunidades abertas têm")}{" "}
                  atestado casado agora. Cada uma casa com até três atestados, e o
                  primeiro — o melhor par — é o que decide a afinidade do score.
                  Contam só as oportunidades que estão nas abas: as encerradas e as
                  de score abaixo de 50 ficam de fora, pelas regras da Nectar.
                </>
              ) : uso.error ? (
                "Não deu para ler os cruzamentos agora."
              ) : (
                "Lendo os cruzamentos…"
              )}
            </p>
          </div>
        </details>
      </div>

      {/* O TIPO DE DOCUMENTO COMO ABAS, o mesmo desenho das de Oportunidades:
          uma linha com filete, a ativa sublinhada no azul. No celular a linha
          rola para os lados. */}
      <Abas
        rotulo="Tipo de documento"
        ativa={natureza}
        aoMudar={(v) => muda({ natureza: v })}
        abas={[{ valor: "", varios: "Todos" }, ...NATUREZAS].map((n) => ({
          valor: n.valor as string,
          rotulo: n.varios,
          conta: porNatureza.get(n.valor) ?? 0,
        }))}
      />

      {/* AS FERRAMENTAS DA LISTA, numa linha: dois seletores (linha de
          atuação e ordem) e a troca de organização. Eram quatro grupos de
          botões dentro de uma caixa. */}
      <div className={css.ferramentas}>
        <Seletor
          className={css.seletor}
          rotulo="Linha de atuação"
          valor={linha}
          aoMudar={(v) => muda({ linha: v })}
          opcoes={[
            { valor: "", rotulo: "Todas", conta: porLinha.get("") ?? 0 },
            ...linhasPossiveis.map((l) => ({ valor: l, rotulo: l, conta: porLinha.get(l) ?? 0 })),
          ]}
        />

        <Seletor
          className={css.seletor}
          rotulo="Ordenar por"
          valor={ordem}
          aoMudar={(v) => muda({ ordem: v })}
          opcoes={[
            { valor: "", rotulo: "Mais recentes" },
            { valor: "cruzamentos", rotulo: "Mais usados nos cruzamentos" },
          ]}
        />

        {filtrando && (
          <Botao variante="texto" onClick={limpar}>
            Limpar filtros
          </Botao>
        )}

        <div className={css.vista} role="group" aria-label="Organização da lista">
          <BotaoIcone rotulo="Lista" ativo={!agrupar} onClick={() => muda({ agrupar: "" })}>
            <IconeGrade tamanho={15} />
          </BotaoIcone>
          <BotaoIcone rotulo="Por linha de atuação" ativo={agrupar}
                      onClick={() => muda({ agrupar: "linha" })}>
            <IconeLinhas tamanho={15} />
          </BotaoIcone>
        </div>
      </div>

      {erro ? (
        <Aviso tom="erro" titulo="Não deu para ler os atestados.">
          {erro instanceof Error ? erro.message : String(erro)}
        </Aviso>
      ) : !buscados.data ? (
        <Vazio>Lendo o acervo…</Vazio>
      ) : !itens.length ? (
        total === 0 ? (
          <Vazio titulo="O acervo está vazio.">
            Nenhum atestado foi carregado ainda. A carga lê a planilha do
            acervo com <code>nectuno-atestados</code>.
          </Vazio>
        ) : (
          <Vazio titulo="Nenhum atestado casa com esses filtros.">
            A busca procura no título, na atividade e no órgão.{" "}
            <button type="button" className={css.limparTexto} onClick={limpar}>
              Limpar filtros
            </button>
          </Vazio>
        )
      ) : agrupar ? (
        // Sem virtualizar, ao contrário da lista de licitações: são ~201
        // cartões que não crescem sozinhos (é documento assinado por cliente,
        // não coleta), e com altura livre cada cartão mostra todas as linhas
        // de atuação em vez de cortá-las numa altura fixa.
        <div className={css.grupos}>
          <NotaDs>
            Um atestado com mais de uma linha de atuação aparece em cada uma
            delas.
          </NotaDs>
          {grupos.map(([nome, lista]) => (
            <SecaoDs key={nome} titulo={nome} conta={lista.length}>
              <ul className={css.lista}>
                {lista.map((f) => (
                  <li key={f.codigo}>
                    <CartaoDoAtestado f={f} uso={usoDe(f)} />
                  </li>
                ))}
              </ul>
            </SecaoDs>
          ))}
        </div>
      ) : (
        <ul className={css.lista} aria-label={`${itens.length} atestados`}>
          {itens.map((f) => (
            <li key={f.codigo}>
              <CartaoDoAtestado f={f} uso={usoDe(f)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Os nomes de coluna de `campos_planilha`, para quem lê a ficha. */
const CAMPO: Record<string, string> = {
  titulo: "título",
  atividade: "atividade",
  data_documento: "data",
  orgao: "órgão",
  linhas: "linhas de atuação",
  entregaveis: "entregáveis",
  publico: "público",
  periodo: "período",
  quantitativo: "quantitativo",
};

/**
 * COMO O TEXTO DO PDF FOI OBTIDO. Quatro valores, e dois deles são fracasso
 * declarado (`atestado_texto.metodo`, migração 0010): "ninguém tentou ler" e
 * "tentamos e não deu" não podem virar o mesmo espaço em branco.
 */
function leitura(f: Ficha): string {
  switch (f.leitura) {
    case "camada":
      return "Texto digital do próprio PDF, sem OCR";
    case "ocr":
      return "OCR — o documento é digitalizado";
    case "ilegivel":
      return "Ilegível: nem o OCR conseguiu ler o documento";
    case "sem_pdf":
      return "Sem PDF: não há arquivo ligado a este atestado";
    case null:
      return "O texto deste documento ainda não foi extraído";
    default:
      return f.leitura;
  }
}

/** A aba em que a oportunidade está — a faixa, com o nome que a tela usa. */
const ABA: Record<string, string> = {
  avaliar: "Avaliar",
  passar: "Descartadas",
  // D17 (2026-10-05): teria score de Avaliar, e o edital ainda não foi lido
  // inteiro. O servidor a manda sem score.
  aguardando: "Aguardando leitura",
};
const POSICAO = (p: number) => (p === 1 ? "melhor par" : `${p}º par`);
const semelhanca = (x: number) =>
  x.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });

/** O tipo da exigência, como a leitura do edital o gravou, por extenso. */
const EXIGENCIA: Record<string, string> = {
  objeto: "objeto",
  servico: "serviço",
  entregavel: "entregável",
  quantitativo: "quantitativo",
  qualificacao_tecnica: "qualificação técnica",
};

/**
 * AS PROVAS de que a oportunidade casa com o que o atestado comprova (pedido
 * do Pedro, 2026-10-05, no dia em que a gestão reprovou a maior parte da aba
 * Avaliar): "todos os atestados mostrem no acervo de atestados as provas que a
 * oportunidade em questão casa com as atividades que o NECTAR presta/já
 * prestou anteriormente". Até ali a tela dava a posição do par e um número de
 * semelhança, que não provam nada.
 *
 * São três, da mais forte para a mais fraca, e cada uma diz o que é:
 *   · a gestão indicou o atestado (D11);
 *   · o EDITAL LIDO inteiro e cruzado: a exigência que o edital faz e o trecho
 *     do atestado que a cobre — as duas pontas, literais, conferidas no
 *     servidor. Sem exigência coberta, a leitura NÃO confirma o par, e isso é
 *     dito com todas as letras;
 *   · as palavras em comum, que é só o que o casador mede.
 *
 * E quando o edital ainda não foi lido, a tela diz — para a semelhança por
 * palavras não passar por prova.
 */
function Provas({ o }: { o: OportunidadeDoAtestado }) {
  const l = o.leitura;
  return (
    <div className={css.provas}>
      {o.indicado && (
        <p>
          <b>Indicado pela gestão</b> como o atestado que comprova: {o.indicado.por}, em{" "}
          {new Date(o.indicado.em).toLocaleDateString("pt-BR")}
          {o.indicado.nota ? ` — “${o.indicado.nota}”` : ""}.
        </p>
      )}
      {!l ? (
        <p>
          <b>Edital ainda não lido inteiro e cruzado.</b> Por enquanto o que liga esta
          oportunidade ao atestado são só as palavras em comum.
        </p>
      ) : !l.perguntado ? (
        <p>
          <b>Pelo edital lido:</b> a matriz não perguntou por este atestado —{" "}
          {l.finalistas
            ? "ele não ficou entre os finalistas que a seleção escolheu para este edital. Não é o mesmo que não cobre."
            : "o tipo de atividade dele é diferente do tipo do edital."}
        </p>
      ) : l.exigencias.length === 0 ? (
        <p className={css.naoConfirma}>
          <b>Pelo edital lido: não confirmado.</b> O edital foi lido inteiro e cruzado, e
          nenhuma exigência dele é coberta por este atestado.
          {o.posicao != null ? " A ligação que sobra é só por palavras." : ""}
        </p>
      ) : (
        <details open={l.exigencias.length <= 2}>
          <summary>
            <b>Pelo edital lido:</b> cobre{" "}
            {[
              l.total > 0 ? `${plural(l.total, "exigência", "exigências")} por inteiro` : null,
              l.parcial > 0 ? `${plural(l.parcial, "exigência", "exigências")} em parte` : null,
            ]
              .filter(Boolean)
              .join(" e ")}
            {l.certificada ? "" : " (leitura ainda não certificada)"}
          </summary>
          <ol>
            {l.exigencias.map((e) => (
              <li key={e.n}>
                <span className={css.provaTipo}>
                  E{e.n} · {EXIGENCIA[e.tipo] ?? e.tipo} ·{" "}
                  {e.cobertura === "total" ? "por inteiro" : "em parte"}
                  {e.reconferido ? " · reconferido" : ""}
                </span>
                <span>
                  <i>O edital pede:</i> “{e.citacao}”
                </span>
                <span>
                  <i>O atestado diz:</i> {e.prova ? `“${e.prova}”` : "trecho não gravado"}
                </span>
                {e.rebaixado && (
                  <span>
                    <i>Em parte porque:</i> {e.rebaixado}
                  </span>
                )}
                {e.falta && (
                  <span>
                    <i>Falta para cobrir por inteiro:</i> {e.falta}
                  </span>
                )}
              </li>
            ))}
          </ol>
          {l.mais > 0 && (
            <p>
              e mais {plural(l.mais, "exigência", "exigências")} — todas estão na leitura
              integral, na tela da oportunidade.
            </p>
          )}
        </details>
      )}
      {o.radicais.length > 0 && (
        <p>
          <b>Por palavras:</b> {plural(o.radicais.length, "radical", "radicais")} em comum
          com o objeto — {o.radicais.join(", ")}. Palavra em comum não é serviço em comum.
        </p>
      )}
    </div>
  );
}

/**
 * AS OPORTUNIDADES EM QUE ESTE ATESTADO APARECE (pedido do Pedro, 2026-09-29),
 * da mais bem pontuada para a menos. Cada uma com a posição dele entre os
 * pares — o melhor par decide a afinidade — e a semelhança medida.
 *
 * E COM AS PROVAS de cada ligação (`Provas`, 2026-10-05). Entram também as
 * que o casador de palavras não trouxe: a que só o edital lido achou e a que
 * só a gestão indicou vêm sem posição.
 *
 * Só as que as abas mostram: a lista nova não é o caminho por onde a de score
 * abaixo de 50 (Regra 1) ou a encerrada (D1) voltariam à tela. E dito, para
 * que "nenhuma" não seja lido como "este atestado nunca serviu".
 */
function OportunidadesCruzadas({
  ops,
  carregando,
  erro,
}: {
  ops?: OportunidadeDoAtestado[];
  carregando: boolean;
  erro: unknown;
}) {
  if (carregando) return <p className={css.pasta}>Procurando nos cruzamentos…</p>;
  if (erro || !ops) {
    return (
      <p className={css.erro}>
        <b>Não deu para ler os cruzamentos.</b>{" "}
        {erro instanceof Error ? erro.message : ""}
      </p>
    );
  }
  const melhor = ops.filter((o) => o.posicao === 1).length;
  const avaliar = ops.filter((o) => o.faixa === "avaliar").length;
  const lidas = ops.filter((o) => o.leitura).length;
  const confirmadas = ops.filter((o) => (o.leitura?.exigencias.length ?? 0) > 0).length;
  const aviso = (
    <p className={css.pasta}>
      Contam só as oportunidades que estão nas abas agora: abertas e com score
      50 ou mais. As encerradas e as de score abaixo de 50 ficam de fora, pelas
      regras da Nectar.
    </p>
  );
  if (!ops.length) {
    return (
      <>
        <p className={painel.prosa}>
          Nenhuma oportunidade aberta cruza com este atestado agora.
        </p>
        {aviso}
      </>
    );
  }
  return (
    <>
      <p className={css.resumoUso}>
        Aparece em <b>{plural(ops.length, "oportunidade", "oportunidades")}</b>
        {melhor > 0 ? (
          <>
            ; é o melhor par em <b>{melhor}</b> — o que decide a afinidade do score
          </>
        ) : (
          "; em nenhuma é o melhor par"
        )}
        {avaliar > 0 ? (
          <>
            {" "}
            — e <b>{avaliar}</b> {avaliar === 1 ? "está" : "estão"} em Avaliar.
          </>
        ) : (
          "."
        )}
      </p>
      {/* A CONTA DAS PROVAS, antes da lista: em quantas o edital foi lido, e em
          quantas a leitura confirma. Sem isto, "aparece em 12" se lia como
          doze oportunidades comprovadas — e é só a semelhança por palavras. */}
      <p className={css.resumoUso}>
        {lidas === 0 ? (
          "Nenhuma delas teve o edital lido inteiro e cruzado ainda: o que há abaixo são ligações por palavras, não provas."
        ) : (
          <>
            O edital foi lido inteiro e cruzado em <b>{lidas}</b> delas; em{" "}
            <b>{confirmadas}</b> a leitura confirma este atestado, com a exigência do
            edital e o trecho dele.
          </>
        )}
      </p>
      <ul className={css.ops} aria-label={plural(ops.length, "oportunidade", "oportunidades")}>
        {ops.map((o) => (
          <li key={o.id}>
            <Link to={`/licitacao/${o.id}`} className={css.op}>
              <Nota valor={o.score} faixa={o.faixa} recomendacao={o.recomendacao} />
              <span className={css.opCorpo}>
                <span className={css.opCabeca}>
                  {o.codigo && <b>{o.codigo}</b>}
                  <span>{o.faixa ? ABA[o.faixa] ?? o.faixa : "sem aba"}</span>
                  {/* SEM POSIÇÃO: o atestado não é par do score desta — ela
                      está aqui pelo edital lido ou pela indicação da gestão,
                      e as provas dizem qual. */}
                  {o.posicao != null ? (
                    <span className={o.posicao === 1 ? css.melhor : undefined}>
                      {POSICAO(o.posicao)}
                      {o.pares > 1 ? ` de ${o.pares}` : ""}
                    </span>
                  ) : (
                    <span>fora dos pares do score</span>
                  )}
                  {o.semelhanca != null && <span>semelhança {semelhanca(o.semelhanca)}</span>}
                  {o.lado === "edital" && <span>pelo trecho do edital</span>}
                  {o.lado === "leitura" && <span>pela leitura integral</span>}
                  {o.lado === "matriz" && <span>pela matriz: cobertura sozinho</span>}
                </span>
                <span className={css.opObjeto}>{o.objeto}</span>
                <span className={css.orgao}>
                  {o.orgao ?? "Órgão não identificado"}
                  {o.uf ? ` · ${o.uf}` : ""}
                  {o.encerramento
                    ? ` · encerra ${new Date(o.encerramento).toLocaleDateString("pt-BR")}`
                    : ""}
                  {o.valor_estimado ? ` · ${o.valor_estimado}` : " · valor não informado"}
                </span>
              </span>
            </Link>
            <Provas o={o} />
          </li>
        ))}
      </ul>
      {aviso}
    </>
  );
}

/** Um campo da ficha. Ausente é DITO, e não some: vazio se lê "não há". */
function Campo({ rotulo, valor, larga }: { rotulo: string; valor?: string | null; larga?: boolean }) {
  return (
    <div className={larga ? painel.larga : undefined}>
      <dt>{rotulo}</dt>
      <dd className={valor ? undefined : css.ausente}>{valor || "não identificado"}</dd>
    </div>
  );
}

/**
 * A ficha de um atestado, em página própria — `/atestados/199` —, pelo mesmo
 * motivo de `/licitacao/:id`: um atestado se cita numa conversa ("usa o #199")
 * e o link tem de levar a ele.
 *
 * A ORDEM É A DA FICHA DO TERMINAL (`consulta.escreve`): título e data no alto,
 * a atividade, o resto do retrato, e a procedência FECHANDO a ficha — quem
 * consulta quer o retrato primeiro, e só depois pergunta "de onde veio isto?".
 */
export function AtestadoFicha() {
  const { codigo = "" } = useParams();
  const cliente = useQueryClient();
  // DUAS LEITURAS, E NÃO UMA PÁGINA LONGA: o retrato do atestado, e as
  // oportunidades em que ele aparece — que podem ser dezenas, cada uma com as
  // provas dela, e empurravam a ficha para baixo da dobra.
  const [aba, setAba] = useState<"ficha" | "oportunidades">("ficha");

  const { data: f, isPending, error } = useQuery({
    queryKey: ["atestado", codigo],
    queryFn: () => api.atestado(codigo),
    // Vindo da lista, a ficha já está no cache dela: aparece na hora, e a
    // consulta própria só confirma.
    placeholderData: () =>
      cliente
        .getQueryData<PaginaFichas>(chaveLista(""))
        ?.itens.find((x) => noCaminho(x.codigo) === noCaminho(codigo)),
  });
  // Consulta à parte: a ficha aparece sem esperar pelos cruzamentos.
  const ops = useQuery({
    queryKey: ["atestado", codigo, "oportunidades"],
    queryFn: () => api.oportunidadesDoAtestado(codigo),
  });

  const voltar = (
    <Migalhas
      itens={[
        { rotulo: "Atestados", para: "/atestados" },
        { rotulo: `Atestado ${f?.codigo ?? codigo}` },
      ]}
    />
  );

  if (isPending) return <p className={pagina.espera}>Carregando…</p>;

  if (error || !f) {
    const naoAchou = error instanceof ErroApi && error.status === 404;
    return (
      <div className={pagina.erro}>
        <h1>{naoAchou ? "Esse atestado não está no acervo" : "Não deu para abrir"}</h1>
        <p>
          {naoAchou
            ? `Nenhum atestado com o código #${noCaminho(codigo)}.`
            : error instanceof Error
              ? error.message
              : "erro desconhecido"}
        </p>
        <Link to="/atestados">Voltar para os atestados</Link>
      </div>
    );
  }

  const prova = f.comprova_capacidade;
  const planilha = f.campos_planilha.map((c) => CAMPO[c] ?? c);
  const nomeArquivo = f.arquivo?.split("/").pop() ?? null;
  const pasta = f.arquivo?.includes("/") ? f.arquivo.slice(0, f.arquivo.lastIndexOf("/")) : null;

  return (
    <article className={pagina.pagina}>
      {voltar}
      <div className={pagina.folha}>
        {/* A escala de leitura da tela dedicada (`leituraAmpla`), a mesma da
            licitação: aqui se lê para citar, não para varrer. */}
        <div className={painel.leituraAmpla}>
          <p className={css.linhaCodigo}>
            <b>{f.codigo}</b>
            <span>
              {f.data ?? "sem data"}
              {f.data_precisao === "ano" ? " (só o ano)" : ""}
            </span>
            <SeloNatureza natureza={f.natureza} />
          </p>
          <h1 className={css.tituloFicha}>{f.titulo || "(sem título)"}</h1>
          <p className={css.orgaoFicha}>{f.orgao ?? "Órgão não identificado"}</p>

          {/* O PAPEL DISCORDA DO CATÁLOGO. Nulo não é "confere": é "não
              havia leitura do PDF com que comparar", e aí não há o que
              avisar. */}
          {f.orgao_confere === false && f.orgao_pdf && (
            <p className={painel.aviso}>
              <b>O órgão do PDF diverge.</b> O documento diz «{f.orgao_pdf}»; o
              órgão acima é o do catálogo. Confira qual vale antes de citar
              este atestado.
            </p>
          )}

          {f.natureza === "projeto" ? (
            // O PROJETO DECLARADO (D14): o aviso é outro. Ele não é um papel
            // fraco, como a declaração — é um serviço executado cujo papel
            // não está aqui.
            <p className={`${painel.aviso} ${css.avisoProva}`}>
              <b>Projeto executado, sem atestado no sistema.</b> A gestão informou
              que este serviço foi executado, e por isso ele entra no cruzamento e
              conta no score das oportunidades. O atestado não está no acervo:
              antes de usá-lo numa proposta, é preciso obtê-lo com o cliente.
            </p>
          ) : (
            !prova && (
              <p className={`${painel.aviso} ${css.avisoProva}`}>
                <b>
                  {nomeDaNatureza(f.natureza).replace(/^./, (c) => c.toUpperCase())}, não
                  atestado.
                </b>{" "}
                Está no acervo, mas {NAO_COMPROVA}: não é a prova que o edital pede
                quando exige atestado.
              </p>
            )
          )}

          <Etiquetas linhas={f.linhas} />

          <div className={painel.abas} role="tablist" aria-label="Seções do atestado">
            {([
              ["ficha", "Ficha do atestado"],
              ["oportunidades", `Oportunidades${ops.data ? ` (${ops.data.length})` : ""}`],
            ] as const).map(([chave, rotulo]) => (
              <button
                key={chave}
                type="button"
                role="tab"
                aria-selected={aba === chave}
                className={painel.aba}
                onClick={() => setAba(chave)}
              >
                {rotulo}
              </button>
            ))}
          </div>

          <div role="tabpanel" className={painel.painelAba}>
            {aba === "ficha" && (
              <>
                <Secao titulo="Atividade">
                  <p className={painel.prosa}>{f.atividade || "não identificada"}</p>
                </Secao>

                <Secao titulo="Ficha">
                  <dl className={painel.ficha}>
                    <div className={painel.larga}>
                      <dt>Entregáveis</dt>
                      {f.entregaveis.length ? (
                        <dd>
                          <ul className={painel.itens}>
                            {f.entregaveis.map((x, i) => (
                              <li key={i}>{x}</li>
                            ))}
                          </ul>
                        </dd>
                      ) : (
                        <dd className={css.ausente}>não identificados</dd>
                      )}
                    </div>
                    <Campo rotulo="Público" valor={f.publico} larga />
                    <Campo rotulo="Período" valor={f.periodo} />
                    <Campo rotulo="Quantitativo" valor={f.quantitativo} />
                    <Campo rotulo="UF" valor={f.uf} />
                  </dl>
                </Secao>

                {/* O PROJETO DECLARADO NÃO TEM DOCUMENTO (D14): no lugar da leitura do
                    PDF, a ficha diz quem informou e de onde saiu a descrição. */}
                {f.natureza === "projeto" && (
                  <Secao titulo="De onde veio">
                    <dl className={painel.ficha}>
                      <Campo
                        rotulo="Documento"
                        valor="Não há: o atestado deste projeto não está no sistema"
                        larga
                      />
                      <Campo
                        rotulo="Informado por"
                        valor={[
                          f.declarado_por,
                          f.declarado_em ? new Date(f.declarado_em).toLocaleDateString("pt-BR") : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      />
                      <Campo rotulo="Fonte da descrição" valor={f.fonte ?? null} larga />
                    </dl>
                  </Secao>
                )}

                {f.natureza !== "projeto" && (
                <Secao titulo="Documento">
                  {f.leitura_duvidosa && (
                    <p className={painel.aviso}>
                      <b>Leitura duvidosa.</b> A confiança média do OCR ficou abaixo
                      de 80, ou mais de 15% das palavras saíram com leitura incerta. Os
                      campos acima podem ter erro de leitura: confira no documento
                      original antes de citar este atestado.
                    </p>
                  )}
                  <dl className={painel.ficha}>
                    <Campo rotulo="Leitura" valor={leitura(f)} larga />
                    {f.confianca_ocr !== null && (
                      <Campo
                        rotulo="Confiança do OCR"
                        valor={`${f.confianca_ocr.toLocaleString("pt-BR")} de 100`}
                      />
                    )}
                    <Campo
                      rotulo="No catálogo"
                      valor={[f.entidade_catalogo, f.ano_catalogo].filter(Boolean).join(" · ")}
                    />
                    {planilha.length > 0 && (
                      <Campo
                        rotulo="Vieram da planilha, e não do PDF"
                        valor={planilha.join(", ")}
                        larga
                      />
                    )}
                    {/* SÓ O NOME DO ARQUIVO, e não o arquivo. Os PDFs moram na pasta
                        `backend/atestados/` da máquina que fez a carga: ela fica fora
                        do git e, por isso, fora da imagem publicada — e nenhum volume
                        a monta. Um link aqui abriria 404 em produção. O nome serve
                        para achar o original no acervo em disco. */}
                    <div className={painel.larga}>
                      <dt>Arquivo</dt>
                      {nomeArquivo ? (
                        <dd>
                          <span className={css.arquivo}>{nomeArquivo}</span>
                          {pasta && <span className={css.pasta}>em {pasta}/</span>}
                          <span className={css.pasta}>
                            O original não é servido pelo sistema publicado: o nome é
                            para achá-lo no acervo em disco.
                          </span>
                        </dd>
                      ) : (
                        <dd className={css.ausente}>nenhum PDF ligado a este atestado</dd>
                      )}
                    </div>
                  </dl>
                </Secao>
                )}
              </>
            )}
            {aba === "oportunidades" && (
              <Secao titulo="Oportunidades em que aparece">
                <OportunidadesCruzadas
                  ops={ops.data}
                  carregando={ops.isPending}
                  erro={ops.error}
                />
              </Secao>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
